import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import SaveIcon from '@mui/icons-material/Save';
import styled from 'styled-components';
import { SearchableSelectInput } from '../../components';
import { SearchableSelectInputProps } from '../../components/SearchableSelectInput';
import { useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Election } from '../../app/feathers/elections/Election';
import { Race } from '../../app/feathers/races/Race';
import { District } from '../../app/feathers/districts/District';
import { seatIdentifierTypeWritten } from '../../app/util/race-display';

interface RaceCellProps {
  raceMatch: {
    race?: string;
    createAsNew: boolean;
  } | null;
  raceInfo?: Race; // Add raceInfo prop
  loading: boolean;
  election: Election;
  onRaceChange?: (groupLabel: string, raceMatch: { race: string; createAsNew: boolean } | null) => void;
  groupLabel: string;
}

type RaceOption = {
  text: string;
  subText?: string;
  _id: string;
  raceObj: Race;
};

type RaceSelectChangeEvent = { target: { value: RaceOption } };
const RaceSearchableSelectInput = SearchableSelectInput as unknown as React.FC<SearchableSelectInputProps<RaceOption>>;

const RaceCell: React.FC<RaceCellProps> = ({ raceMatch, raceInfo, loading, election, onRaceChange, groupLabel }) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [isEditing, setIsEditing] = useState(false);
  const [editMode, setEditMode] = useState<'create' | 'existing'>('create');
  const [selectedRace, setSelectedRace] = useState<RaceOption | null>(null);
  const [raceOptions, setRaceOptions] = useState<RaceOption[]>([]);
  const [raceOptionsLoading, setRaceOptionsLoading] = useState(false);

  const createRaceOption = (race: Race): RaceOption => {
    const officeText = [
      race.officeName,
      race.positionData?.special ? '(Unexpired)' : null,
      race.party ? `(${race.party})` : null
    ].filter(Boolean).join(' ');
    const seatIdentifierText = [
      seatIdentifierTypeWritten(race.positionData?.seatIdentifierType),
      race.positionData?.seatIdentifier
    ].filter(Boolean).join(' ');

    return {
      text: officeText,
      subText: [race.district?.longName, seatIdentifierText].filter(Boolean).join(' • '),
      _id: race._id,
      raceObj: race
    };
  };

  const searchRaces = async (searchText: string) => {
    if (!feathers || !election) return;

    setRaceOptionsLoading(true);
    try {
      const query = {
        election: election.key,
        $skip: 0,
        $limit: 100,
        ...(searchText
          ? { $or: [ 
              { candidatesSearchTerm: { $search: searchText } }, 
              { officeSearchTerm: { $search: searchText } } 
            ] }
          : {}
        ),
        $sort: { date: -1 }
      };
      
      const races = await feathers.getService('races').find({ query });
      const options = races.data
        .filter((r) => r.district?.type !== 'country')
        .map(createRaceOption);
      
      setRaceOptions(options);
    } catch (error) {
      console.error('Error searching races:', error);
      setRaceOptions([]);
    } finally {
      setRaceOptionsLoading(false);
    }
  };

  const handleRaceChange = (e: RaceSelectChangeEvent) => {
    const { value } = e.target;
    setSelectedRace(value);
  };

  const handleSave = () => {
    if (onRaceChange) {
      let newRaceMatch: { race: string; createAsNew: boolean } | null = null;
      
      if (editMode === 'create') {
        newRaceMatch = { race: '', createAsNew: true };
      } else if (editMode === 'existing' && selectedRace) {
        newRaceMatch = { race: selectedRace._id, createAsNew: false };
      }
      
      onRaceChange(groupLabel, newRaceMatch);
    }
    
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Skeleton variant="text" width={100} height={24} />
    );
  }

  if (isEditing) {
    return (
      <Box sx={{ 
        minWidth: 200, 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 1,
        position: 'relative'
      }}>
        <Box sx={{ flex: 1 }}>
          <RadioGroup
            value={editMode}
            onChange={(e) => setEditMode(e.target.value as 'create' | 'existing')}
            sx={{ '& .MuiFormControlLabel-root': { margin: 0, minHeight: '24px' } }}
          >
            <FormControlLabel
              value="create"
              control={<Radio size="small" sx={{ padding: '2px' }} />}
              label="Create as new race"
              sx={{ fontSize: '11px', marginBottom: '2px' }}
            />
            <FormControlLabel
              value="existing"
              control={<Radio size="small" sx={{ padding: '2px' }} />}
              label="Mark as existing race"
              sx={{ fontSize: '11px' }}
            />
          </RadioGroup>
          
          {editMode === 'existing' && (
            <Box sx={{ mt: 0.5 }}>
              <RaceSearchableSelectInput
                name="race"
                onChangeSearchText={searchRaces}
                value={selectedRace}
                onChange={handleRaceChange}
                optionsLoading={raceOptionsLoading}
                options={raceOptions}
                placeholder="Search for existing race..."
                style={{ width: '100%' }}
                size="small"
              />
            </Box>
          )}
        </Box>
        
        <IconButton
          size="small"
          onClick={handleSave}
          sx={{ 
            padding: '4px',
            color: '#6a1b9a',
            '&:hover': { backgroundColor: '#f3e5f5' }
          }}
        >
          <SaveIcon sx={{ fontSize: '16px' }} />
        </IconButton>
      </Box>
    );
  }

  // Handle click to enter editing mode
  const handleClick = () => {
    setIsEditing(true);
    if (raceMatch?.createAsNew) {
      setEditMode('create');
    } else if (raceMatch?.race) {
      setEditMode('existing');
      // TODO: Set selectedRace based on existing raceMatch.race
    } else {
      setEditMode('create');
    }
  };

  if (raceMatch?.createAsNew) {
    return (
      <PurplePill onClick={handleClick} style={{ cursor: 'pointer' }}>
        <Typography variant="body2" sx={{ color: '#6a1b9a', fontSize: '12px' }}>
          Create as new race
        </Typography>
      </PurplePill>
    );
  }

  if (raceMatch?.race) {
    return (
      <PurplePill onClick={handleClick} style={{ cursor: 'pointer' }}>
        <Typography variant="body2" sx={{ color: '#6a1b9a', fontSize: '12px' }}>
          {raceInfo ? `${raceInfo.longName}${raceInfo.party ? ` (${raceInfo.party})` : ''}` : raceMatch.race}
        </Typography>
      </PurplePill>
    );
  }

  // No raceMatch provided - needs attention
  return (
    <NeedsAttentionPill onClick={handleClick} style={{ cursor: 'pointer' }}>
      <Typography variant="body2" sx={{ color: '#d32f2f', fontSize: '12px' }}>
        Needs attention
      </Typography>
    </NeedsAttentionPill>
  );
};

const PurplePill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid #6a1b9a;
  border-radius: 16px;
  background-color: #fff;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f3e5f5;
    border-color: #4a148c;
  }
`;

const NeedsAttentionPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid #d32f2f;
  border-radius: 16px;
  background-color: #ffebee;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #ffcdd2;
    border-color: #b71c1c;
  }
`;

export default RaceCell; 